import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowBack,
  Person,
  Schedule,
  CalendarToday,
  CheckCircle,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { appointmentAPI, Appointment, AppointmentStatus, ConsultationType } from '../services/apiService';

const BookingDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<Appointment[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Appointment | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [serviceFilter, setServiceFilter] = useState<string>(user?.serviceType || 'fmla');
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    if (!user?.paymentAmount || user.paymentAmount <= 0) {
      navigate('/payment-required');
      return;
    }
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      setPaymentSuccess(true);
      setSearchParams({});
    }
  }, [user, navigate, searchParams, setSearchParams]);

  useEffect(() => {
    if (user?.serviceType && user.serviceType !== serviceFilter) {
      setServiceFilter(user.serviceType);
    }
  }, [user?.serviceType]);

  useEffect(() => {
    loadAvailableSlots();
  }, [serviceFilter]);

  const loadAvailableSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use enhanced API service for slot discovery
      const availableAppointments = await appointmentAPI.getAvailableSlots(user?.employmentState);
      
      // Filter for service type and future dates only
      const filteredSlots = availableAppointments.filter((apt: Appointment) => 
        apt.serviceType === serviceFilter &&
        new Date(apt.dateTime) > new Date() // Future dates only
      );

      setAvailableSlots(filteredSlots);
    } catch (err) {
      setError('Failed to load available appointment slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSlot = (slot: Appointment) => {
    setSelectedSlot(slot);
    setConfirmDialog(true);
  };

  const handleConfirmBooking = async () => {
    if (!selectedSlot || booking) return;

    try {
      setBooking(true);
      setLoading(true);
      setError(null);

      // Book the specific appointment slot
      await appointmentAPI.book({ appointmentId: selectedSlot.appointmentId });
      
      setSuccess(true);
      setConfirmDialog(false);
      
      // Redirect to appointments page after brief delay
      setTimeout(() => {
        navigate('/appointments');
      }, 2000);
      
    } catch (err) {
      setError('Failed to book appointment. Please try again.');
      setConfirmDialog(false);
    } finally {
      setLoading(false);
      setBooking(false);
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getServiceTypeLabel = (serviceType: string) => {
    return serviceType === 'fmla-std' ? 'FMLA + STD Bundle' : 'FMLA Certification';
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <Card sx={{ py: 6 }}>
          <CardContent>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Appointment Booked Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Your FMLA consultation has been scheduled.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You'll receive a confirmation email with provider details and meeting information.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/patient/dashboard')}
          sx={{ mr: 2 }}
        >
          Back to Dashboard
        </Button>
        <div>
          <Typography variant="h3" component="h1">
            Book Your Appointment
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Select an available time slot with a licensed provider in {user?.employmentState || 'your state'}
          </Typography>
        </div>
      </Box>

      {/* Service Information */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'white' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom>
                {getServiceTypeLabel(user?.serviceType || 'fmla')}
              </Typography>
              <Typography variant="body1">
                Service Cost: ${user?.paymentAmount || 100} (Already Paid ✓)
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Licensed providers available in {user?.employmentState || 'your state'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign="right">
              <Chip 
                label="Payment Confirmed" 
                color="secondary" 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} 
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Service Filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Service Type</InputLabel>
          <Select
            value={serviceFilter}
            label="Service Type"
            onChange={(e) => setServiceFilter(e.target.value)}
          >
            <MenuItem value="fmla">FMLA Certification</MenuItem>
            <MenuItem value="fmla-std">FMLA + STD Bundle</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {paymentSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
          Payment successful! Now select an available time slot to book your consultation.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Available Slots */}
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Available Time Slots
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : availableSlots.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Available Slots Right Now
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              All provider slots are currently booked for {getServiceTypeLabel(serviceFilter)} in {user?.employmentState || 'your state'}.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Providers regularly add new availability. Your payment is saved and you can book as soon as slots open up.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={loadAvailableSlots} startIcon={<Schedule />}>
                Refresh Availability
              </Button>
              <Button variant="outlined" href="mailto:support@fmla.doctor?subject=Booking%20Assistance">
                Contact Support
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {availableSlots.map((slot) => (
            <Grid item xs={12} md={6} key={slot.appointmentId}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
                onClick={() => handleSelectSlot(slot)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarToday color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      {formatDateTime(slot.dateTime)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Person color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Licensed Provider
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Duration: {slot.duration} minutes
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Chip 
                      label={getServiceTypeLabel(slot.serviceType)}
                      color="primary" 
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialog} 
        onClose={() => setConfirmDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Confirm Appointment Booking
        </DialogTitle>
        <DialogContent>
          {selectedSlot && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {formatDateTime(selectedSlot.dateTime)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Provider: Licensed FMLA Provider
              </Typography>
              <Typography variant="body1" gutterBottom>
                Service: {getServiceTypeLabel(selectedSlot.serviceType)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Duration: {selectedSlot.duration} minutes
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Once booked, you'll receive an email with video consultation details and assessment instructions.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmBooking}
            variant="contained"
            disabled={loading || booking}
          >
            {loading || booking ? <CircularProgress size={20} /> : 'Confirm Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BookingDashboardPage;